/**
 * Outreach v2 — API service layer.
 *
 * Typed axios wrappers over the backend outreach API (spec §15). The module
 * auto-mounts at `/v2/outreach` → public path `/api/v2/outreach` (the axios
 * instance prepends `/api` via getServerUrl). Every call is org-scoped on the
 * server by `req.organizationId`.
 *
 * P1–P4 endpoints (accounts, campaigns, sequences, enrollments, suppressions,
 * launch readiness) are wired now. CRM inbox / analytics / sequence generate
 * stream land in later prompts — the SSE helper below is provided up front so
 * those (and the NL sequence builder) can reuse it, mirroring icp.api.ts.
 */

import api from '@/services/api';
import { getRequestContext } from '@/lib/http';
import { getServerUrl } from '@/utils/';
import { accessTokenKey } from '@auth/constants';
import type {
    OutreachAccount,
    ConnectAccountPayload,
    ConnectAccountResult,
    ClaimAccountResult,
    UpdateAccountPayload,
    OutreachCampaign,
    CreateCampaignPayload,
    ListCampaignsParams,
    JobCampaignState,
    CsvImportRow,
    ImportContactRow,
    ImportContactsResult,
    OutreachSequence,
    OutreachProspect,
    ProspectProgress,
    ProspectStage,
    ProspectIdentityPatch,
    EnrollCandidatesPayload,
    JobApplicant,
    EnrollmentStatus,
    OutreachSuppression,
    AddSuppressionPayload,
    ListSuppressionsParams,
    LaunchReadiness,
    StopReason,
    InboxParams,
    InboxResponse,
    UnmatchedEvent,
    UnmatchedEventsResponse,
    ThreadResponse,
    ThreadActivityResponse,
    ReplyPayload,
    ReplyResult,
    DraftReplyPayload,
    DraftReplyResult,
    Conversation,
    ConversationStatus,
    InterestLevel,
    NotificationSummary,
    OpsHealth,
    CampaignAnalytics,
    JobAnalytics,
    OutreachOverview,
    AccountAnalytics,
    MessagePreviewRequest,
    MessagePreviewResult,
    PrewriteView,
    SampleCandidate,
    SendTestResult,
    OutreachTemplateSummary,
    BuilderThreadResponse,
    ApplyTemplateResult,
    ReplyBehaviorView,
    UpdateReplyBehaviorPayload,
    AuditEntry,
    EnrichmentResult,
    ProspectEnrichmentResult,
    EnrichmentJobStatus,
    CampaignEmailEnrichmentSummary,
    SourcingIcpSummary,
    SourcingPreview,
    SourcingJobStatus,
    SourcingCandidate,
    CreateSourcePayload,
    UpdateSourcePayload,
    CampaignSource,
    SourceRun,
    SourceRunResult,
    PromoteResult,
    SearchSourceSummary,
    SearchSourceCandidate,
} from '../types';

const BASE = '/v2/outreach';
/** Enrichment is its own module — auto-mounted at `/v2/enrichment`. */
const ENRICH_BASE = '/v2/enrichment';
/** Mutable progress reads must produce a response body on every request. */
const LIVE_ENRICHMENT_READ = { headers: { 'Cache-Control': 'no-cache' } } as const;
/** Sourcing is its own module — auto-mounted at `/v2/sourcing`. */
const SOURCING_BASE = '/v2/sourcing';

/* ─── Accounts (§15) ─────────────────────────────────────── */

export const accountsApi = {
    list: () => api.get<{ accounts: OutreachAccount[] }>(`${BASE}/accounts`),

    /** Kicks off hosted-auth. In simulated mode the account returns already
     *  connected with `hostedAuthUrl: null`. */
    connect: (payload: ConnectAccountPayload) => api.post<ConnectAccountResult>(`${BASE}/accounts/connect`, payload),

    /** Caps/window/signature edits. The server clamps caps above the safe
     *  ceiling rather than rejecting (§11). */
    update: (id: string, payload: UpdateAccountPayload) =>
        api.patch<{ account: OutreachAccount }>(`${BASE}/accounts/${id}`, payload),

    /** Refresh provider-confirmed LinkedIn seat types and current InMail balance. */
    refreshLinkedInFeatures: (id: string) =>
        api.post<{ account: OutreachAccount }>(`${BASE}/accounts/${id}/linkedin-features/refresh`),

    remove: (id: string) => api.delete<{ success: boolean }>(`${BASE}/accounts/${id}`),

    /** Reconnect a disconnected/error/on_hold account IN PLACE (Stage 1) — reuses
     *  the same row so campaigns/threads stay valid. Returns a hosted-auth URL to
     *  complete (or a bound row in simulated mode). */
    reconnect: (id: string) => api.post<ConnectAccountResult>(`${BASE}/accounts/${id}/reconnect`),

    /** Claim an on_hold account ("Disconnect from all other organizations", Stage 1). */
    claim: (id: string) => api.post<ClaimAccountResult>(`${BASE}/accounts/${id}/claim`),

    /** Today's usage vs cap + health (throttled / errors / disconnects) (§13). */
    analytics: (id: string) => api.get<AccountAnalytics>(`${BASE}/accounts/${id}/analytics`),
};

/* ─── Campaigns (§15) ────────────────────────────────────── */

export const campaignsApi = {
    list: (params?: ListCampaignsParams) => api.get<{ campaigns: OutreachCampaign[] }>(`${BASE}/campaigns`, { params }),

    create: (payload: CreateCampaignPayload) => api.post<{ campaign: OutreachCampaign }>(`${BASE}/campaigns`, payload),

    /** CSV → audience: bulk-upsert uploaded contacts as People (dedupe; no enroll).
     *  Returns the imported People + the LinkedIn-only ones to enrich. */
    importContacts: (id: string, contacts: ImportContactRow[]) =>
        api.post<ImportContactsResult>(`${BASE}/campaigns/${id}/import-contacts`, { contacts }),

    get: (id: string) => api.get<{ campaign: OutreachCampaign }>(`${BASE}/campaigns/${id}`),

    update: (id: string, payload: Partial<OutreachCampaign>) =>
        api.patch<{ campaign: OutreachCampaign }>(`${BASE}/campaigns/${id}`, payload),

    launch: (id: string) =>
        api.post<{ campaign: OutreachCampaign; readiness?: LaunchReadiness }>(`${BASE}/campaigns/${id}/launch`),

    pause: (id: string) => api.post<{ campaign: OutreachCampaign }>(`${BASE}/campaigns/${id}/pause`),

    resume: (id: string) => api.post<{ campaign: OutreachCampaign }>(`${BASE}/campaigns/${id}/resume`),

    archive: (id: string) => api.post<{ campaign: OutreachCampaign }>(`${BASE}/campaigns/${id}/archive`),

    launchReadiness: (id: string) => api.get<LaunchReadiness>(`${BASE}/campaigns/${id}/launch-readiness`),

    /** Campaign summary + per-step + per-channel metrics (§13). */
    analytics: (id: string) => api.get<CampaignAnalytics>(`${BASE}/campaigns/${id}/analytics`),

    /** Job-workspace anchor (Stage 3): THE campaign for a job (deterministic
     *  server-side selection) + roster stage counts. `campaign` is null until
     *  the workspace provisions one. */
    getByJob: (jobId: string) => api.get<JobCampaignState>(`${BASE}/campaigns/by-job/${jobId}`),

    /** Lazily provision the job's anchor campaign (idempotent get-or-create —
     *  concurrent calls converge on one winner server-side). */
    getOrCreateForJob: (jobId: string) =>
        api.post<{ campaign: OutreachCampaign; created: boolean }>(`${BASE}/campaigns/get-or-create`, { jobId }),

    /** "Add to outreach" (Stage 3 P2): move selected people from the job's
     *  Sourced pool into its anchor campaign (get-or-created), review-mode, then
     *  best-effort auto-enrich. `enrichJobId` is null when the enqueue wasn't
     *  possible (e.g. locally without the worker). */
    addSourced: (jobId: string, candidateKeys: string[]) =>
        api.post<{
            campaignId: string;
            added: number;
            skipped: number;
            noLinkedin: number;
            prospectIds: string[];
            enrichJobId: string | null;
            enrichTotal: number;
        }>(`${BASE}/campaigns/add-from-sourced`, { jobId, candidateKeys }),

    /** CSV import → the job's anchor campaign (Stage 3 §2C). Client-parsed rows
     *  ({name,email,linkedinUrl,company,title,phone}), review-mode intake (rows
     *  land at 'sourced'); `autoEnrich` kicks a bulk enrich over the fresh rows. */
    importCsv: (jobId: string, rows: CsvImportRow[], opts?: { autoEnrich?: boolean }) =>
        api.post<{ campaignId: string; runId: string | null; added: number; skipped: number; enrichJobId: string | null }>(
            `${BASE}/campaigns/import-csv`,
            { jobId, rows, autoEnrich: !!opts?.autoEnrich },
        ),
};

/* ─── Job responsiveness (§13, §15) ──────────────────────── */

export const jobAnalyticsApi = {
    /** Aggregate every campaign on a job — best campaigns / channels / segments. */
    get: (jobId: string) => api.get<JobAnalytics>(`${BASE}/jobs/${jobId}/analytics`),

    /** Org-wide rollup + per-job ranked list + account health for the Analytics page. */
    overview: () => api.get<OutreachOverview>(`${BASE}/analytics/overview`),
};

/* ─── Sequence (§15) ─────────────────────────────────────── */

export const sequenceApi = {
    get: (campaignId: string) =>
        api.get<{ sequence: OutreachSequence | null }>(`${BASE}/campaigns/${campaignId}/sequence`),

    update: (campaignId: string, body: Partial<OutreachSequence>) =>
        api.put<{ sequence: OutreachSequence }>(`${BASE}/campaigns/${campaignId}/sequence`, body),

    /** Resolve a node's message for a sample candidate (template or AI draft). */
    preview: (campaignId: string, body: MessagePreviewRequest) =>
        api.post<MessagePreviewResult>(`${BASE}/campaigns/${campaignId}/sequence/preview`, body),

    /** Candidates the recruiter can preview/approve against. */
    sampleCandidates: (campaignId: string, limit = 6) =>
        api.get<{ candidates: SampleCandidate[] }>(`${BASE}/campaigns/${campaignId}/sample-candidates`, {
            params: { limit }
        }),

    /** "Send test to me" — emails the rendered message to the current recruiter. */
    sendTest: (campaignId: string, body: MessagePreviewRequest) =>
        api.post<SendTestResult>(`${BASE}/campaigns/${campaignId}/sequence/send-test`, body),

    /** Seed this campaign's sequence from a saved template (§5). By default the
     *  copy is re-adapted to this campaign's job/ICP while the branch logic is
     *  preserved (pass `reAdapt: false` to copy verbatim). */
    applyTemplate: (campaignId: string, templateId: string, reAdapt = true) =>
        api.post<ApplyTemplateResult>(`${BASE}/campaigns/${campaignId}/sequence/apply-template`, {
            templateId,
            reAdapt
        })
};

/* ─── NL sequence builder (§5, P13) ──────────────────────── *
 * The conversational generate stream is opened via `openOutreachStream`
 * (below) against `/campaigns/:id/sequence/generate`. This wrapper only loads the
 * checkpointed conversation + current tree so the chat resumes on reopen. */

export const sequenceBuilderApi = {
    thread: (campaignId: string) =>
        api.get<BuilderThreadResponse>(`${BASE}/campaigns/${campaignId}/sequence/builder`),
};

/* ─── Reply behavior — admin/agent controls (§4, §8, P13) ── */

export const settingsApi = {
    /** The org's reply behavior as the full per-class view (`outreach.view`). */
    getReplyBehavior: () => api.get<{ replyBehavior: ReplyBehaviorView }>(`${BASE}/settings/reply-behavior`),

    /** Update autonomy mode + escalation rules. Admin-only (`outreach.admin`) —
     *  a 403 means the user can view but not change reply behavior. */
    updateReplyBehavior: (payload: UpdateReplyBehaviorPayload) =>
        api.put<{ replyBehavior: ReplyBehaviorView }>(`${BASE}/settings/reply-behavior`, payload)
};

/* ─── Audit log (§12, P13) ────────────────────────────────── */

export const auditApi = {
    list: (params?: { limit?: number; targetType?: string; targetId?: string; action?: string }) =>
        api.get<{ entries: AuditEntry[] }>(`${BASE}/audit`, { params })
};

/* ─── Reusable templates (§5, §15) ───────────────────────── */

export const templatesApi = {
    list: () => api.get<{ templates: OutreachTemplateSummary[] }>(`${BASE}/templates`),

    /** Save a campaign's current sequence as a reusable, org-scoped template. */
    save: (campaignId: string, name: string) =>
        api.post<{ template: OutreachTemplateSummary }>(`${BASE}/templates`, { campaignId, name }),

    remove: (id: string) => api.delete<{ deleted: boolean }>(`${BASE}/templates/${id}`)
};

/* ─── Prospects (§15, Stage 2 — replaces enrollments) ────── */

export const prospectsApi = {
    enroll: (campaignId: string, payload: EnrollCandidatesPayload) =>
        api.post(`${BASE}/campaigns/${campaignId}/enroll`, payload),

    /** This role's applicants as enrollable outreach candidates (§4). */
    listJobApplicants: (jobId: string) =>
        api.get<{ applicants: JobApplicant[]; total: number }>(`${BASE}/jobs/${jobId}/applicants`),

    /** Paginated (H10): default 200 per page, max 1000. Rows carry denormalized
     *  identity — no populated candidateId, no contactSnapshot. */
    list: (
        campaignId: string,
        params?: {
            status?: EnrollmentStatus;
            stage?: ProspectStage;
            limit?: number;
            skip?: number;
            snapshotAt?: string;
            search?: string;
            bucket?:
                | 'all'
                | 'enrolled'
                | 'ready'
                | 'enriching'
                | 'blocked'
                | 'no_contact'
                | 'duplicated'
                | 'in_sequence'
                | 'paused'
                | 'replied'
                | 'done';
            sort?: 'newest' | 'oldest';
        },
    ) =>
        api.get<{
            prospects: OutreachProspect[];
            total: number;
            campaignTotal?: number;
            counts?: Partial<
                Record<
                    | 'all'
                    | 'enrolled'
                    | 'ready'
                    | 'enriching'
                    | 'blocked'
                    | 'no_contact'
                    | 'duplicated'
                    | 'in_sequence'
                    | 'paused'
                    | 'replied'
                    | 'done',
                    number
                >
            >;
            limit: number;
            skip: number;
            snapshotAt: string | null;
        }>(`${BASE}/campaigns/${campaignId}/prospects`, { params: { ...params, flowStatusVersion: '2' } }),

    /** Per-prospect flow progress for the person drawer: the ordered send steps
     *  with a status each (sent/queued/error/won't-reach/…), timestamps, and
     *  estimated next send. Server-derived to mirror the engine. */
    progress: (id: string) => api.get<ProspectProgress>(`${BASE}/prospects/${id}/progress`, { params: { flowStatusVersion: '2' } }),

    /** Pin recruiter-edited, fully-resolved copy for one unsent advanced-AI step. */
    editPrewrite: (id: string, nodeId: string, fields: { subject?: string; body?: string; inviteNote?: string }) =>
        api.put<{
            prewrite: PrewriteView;
            fields: { subject?: string; body?: string; inviteNote?: string };
        }>(
            `${BASE}/prospects/${id}/steps/${encodeURIComponent(nodeId)}/prewrite`,
            { fields }
        ),

    /** Explicitly discard an edit and generate/pin fresh advanced-AI copy. */
    regeneratePrewrite: (id: string, nodeId: string) =>
        api.post<MessagePreviewResult>(
            `${BASE}/prospects/${id}/steps/${encodeURIComponent(nodeId)}/prewrite/regenerate`
        ),

    /** Promote sourced (review-mode) prospects into the engine — the sourced
     *  table's checkbox action. Guards re-run at promote time. */
    promote: (campaignId: string, prospectIds: string[]) =>
        api.post<PromoteResult>(`${BASE}/campaigns/${campaignId}/prospects/enroll`, { prospectIds }),

    /** Edit a prospect's denormalized identity in place (`outreach.edit`).
     *  Provided keys REPLACE that field wholesale — send the FULL reconstructed
     *  array for emails/phones ([newPrimary, ...rest]); identityHashes are
     *  recomputed server-side. 400 = zero identity anchors left; 409 = a value
     *  already belongs to another prospect in this campaign (E11000). */
    updateIdentity: (id: string, patch: ProspectIdentityPatch) =>
        api.patch<{ prospect: OutreachProspect }>(`${BASE}/prospects/${id}/identity`, patch),

    pause: (id: string) => api.post<{ prospect: OutreachProspect }>(`${BASE}/prospects/${id}/pause`),

    resume: (id: string) => api.post<{ prospect: OutreachProspect }>(`${BASE}/prospects/${id}/resume`),

    stop: (id: string, reason: StopReason = 'manual') =>
        api.post<{ prospect: OutreachProspect }>(`${BASE}/prospects/${id}/stop`, {
            reason,
        }),

    /** Hard-delete a prospect (`outreach.edit`). Status-guarded server-side:
     *  409 `delete_forbidden` for an in-sequence (active/paused) or mid-enrich
     *  row — pause/stop or wait first. */
    remove: (id: string) => api.delete<{ deleted: boolean; prospectId: string }>(`${BASE}/prospects/${id}`),
};

/* ─── Campaign data sources (Stage 2, P2) ─────────────────── *
 * Composable audience sources on a campaign: CSV rows → prospects directly
 * (no Person docs), one-off search pushes from the user's search-v3 shortlists,
 * and scheduled searches. Creating a manual source executes its first run in
 * the same call; every add records an OutreachSourceRun. */

export const sourcesApi = {
    /** Create a source and (csv / search_push / runNow) execute its first run. */
    create: (campaignId: string, payload: CreateSourcePayload) =>
        api.post<SourceRunResult & { source: CampaignSource }>(`${BASE}/campaigns/${campaignId}/sources`, payload),

    /** Re-run an existing source (fresh CSV rows, or newly-arrived shortlist rows). */
    run: (campaignId: string, sourceId: string, payload?: { rows?: ImportContactRow[]; candidateKeys?: string[] }) =>
        api.post<SourceRunResult>(`${BASE}/campaigns/${campaignId}/sources/${sourceId}/run`, payload || {}),

    update: (campaignId: string, sourceId: string, payload: UpdateSourcePayload) =>
        api.patch<{ source: CampaignSource }>(`${BASE}/campaigns/${campaignId}/sources/${sourceId}`, payload),

    /** Remove the source subdoc — its prospects stay (they carry the identity). */
    remove: (campaignId: string, sourceId: string) =>
        api.delete<{ deleted: boolean }>(`${BASE}/campaigns/${campaignId}/sources/${sourceId}`),

    /** Recent runs decorated with LIVE per-run prospect counts (funnel data). */
    listRuns: (campaignId: string, limit = 30) =>
        api.get<{ runs: SourceRun[] }>(`${BASE}/campaigns/${campaignId}/source-runs`, { params: { limit } }),

    /** The current user's search-v3 configs, with pushable-row counts. */
    listSearchSources: () => api.get<{ sources: SearchSourceSummary[] }>(`${BASE}/search-sources`),

    /** Pushable shortlist rows of one config for the selection table. */
    listSearchCandidates: (configId: string, limit = 500) =>
        api.get<{ candidates: SearchSourceCandidate[]; noLinkedin: number; total: number }>(
            `${BASE}/search-sources/${configId}/candidates`,
            { params: { limit } }
        ),
};

/** Legacy alias — the module barrel (index.ts) still re-exports `enrollmentApi`;
 *  new code should import `prospectsApi`. */
export const enrollmentApi = prospectsApi;

/* ─── Contact enrichment (waterfall) ─────────────────────── *
 * Finds + verifies a candidate's email from a LinkedIn URL / name+company so
 * contactless candidates stop falling out of campaigns. On-demand (per candidate)
 * or bulk (enqueue + poll). Mounted at `/v2/enrichment`, not the outreach base. */

export const enrichmentApi = {
    /** On-demand: find + verify one candidate's email (synchronous, ~1–2s).
     *  `force` bypasses the 30-day cache to refresh a stale address. */
    enrichPerson: (personId: string, force = false) =>
        api.post<EnrichmentResult>(`${ENRICH_BASE}/person/${personId}`, { force }),

    /** Prospect-facing enrich (Stage 3): inputs from prospect.identity, the found
     *  email appended via the server's E11000-safe identity writer. */
    enrichProspect: (prospectId: string, force = false) =>
        api.post<ProspectEnrichmentResult>(`${ENRICH_BASE}/prospect/${prospectId}`, { force }),

    /** Bulk prospect enrich — explicit ids, or a campaign/run selector (the CSV
     *  result-phase "Find N missing emails" button; the server resolves the id
     *  set). `matched` vs `total` surfaces the 200-row clamp. Poll bulkStatus. */
    bulkEnrichProspects: (selector: {
        prospectIds?: string[];
        campaignId?: string;
        runId?: string;
        missingEmailOnly?: boolean;
    }) =>
        api.post<{ jobId: string; status: string; total: number; matched: number; clamped: boolean }>(
            `${ENRICH_BASE}/prospects/bulk`,
            selector
        ),

    /** Bulk: enqueue an enrichment run over many candidates → returns a jobId. */
    bulkEnrich: (personIds: string[]) =>
        api.post<{ jobId: string; status: string; total: number }>(`${ENRICH_BASE}/bulk`, { personIds }),

    /** Poll a bulk job's progress. */
    bulkStatus: (jobId: string) =>
        api.get<EnrichmentJobStatus>(`${ENRICH_BASE}/bulk/${jobId}`, LIVE_ENRICHMENT_READ),
};

export const campaignEnrichmentApi = {
    summary: (campaignId: string) =>
        api.get<CampaignEmailEnrichmentSummary>(
            `${ENRICH_BASE}/campaigns/${campaignId}/missing-emails`,
            LIVE_ENRICHMENT_READ
        ),
    start: (campaignId: string) =>
        api.post<{ operation: EnrichmentJobStatus; existing: boolean }>(
            `${ENRICH_BASE}/campaigns/${campaignId}/missing-emails`
        ),
    retry: (operationId: string) =>
        api.post<{ operation: EnrichmentJobStatus; resumedFrom: number }>(
            `${ENRICH_BASE}/campaign-operations/${operationId}/retry`
        ),
    cancel: (operationId: string) =>
        api.post<{ operation: EnrichmentJobStatus }>(
            `${ENRICH_BASE}/campaign-operations/${operationId}/cancel`
        ),
};

/* ─── ICP sourcing (the outreach kickoff front-half) ─────── *
 * Turns a job's ICP into ~100 matching LinkedIn candidates, imports them to the
 * talent pool, bulk-enriches, and enrolls the contactable ones into the campaign
 * — leaving it in a review-before-launch state. NEVER sends; the recruiter still
 * clicks Launch. Its own module, auto-mounted at `/v2/sourcing`. */

export const sourcingApi = {
    /** Read-only ICP summary + the exact Exa query for the kickoff card (no spend). */
    icpSummary: (jobId: string) => api.get<SourcingIcpSummary>(`${SOURCING_BASE}/icp/${jobId}`),

    /** Build the ICP query + source candidates (Exa search only; no enrichment yet). */
    preview: (jobId: string, limit = 100) =>
        api.post<SourcingPreview>(`${SOURCING_BASE}/preview`, { jobId, limit }),

    /** Kick off the phased source → import → enrich → enroll run → returns a jobId.
     *  Pass `candidates` to honor the recruiter's trimmed preview selection. */
    run: (payload: { jobId: string; campaignId: string; limit?: number; candidates?: SourcingCandidate[] }) =>
        api.post<{ jobId: string; status: string; phase: string }>(`${SOURCING_BASE}/run`, payload),

    /** Poll a sourcing run's phased progress. */
    jobStatus: (id: string) => api.get<SourcingJobStatus>(`${SOURCING_BASE}/jobs/${id}`),
};

/* ─── Suppression (§12, §15) ─────────────────────────────── */

export const suppressionApi = {
    list: (params?: ListSuppressionsParams) =>
        api.get<{ suppressions: OutreachSuppression[] }>(`${BASE}/suppressions`, {
            params,
        }),

    add: (payload: AddSuppressionPayload) =>
        api.post<{ suppression: OutreachSuppression }>(`${BASE}/suppressions`, payload),

    remove: (id: string) => api.delete<{ success: boolean }>(`${BASE}/suppressions/${id}`),
};

/* ─── CRM inbox (§9, §15) ─────────────────────────────────── */

export const inboxApi = {
    /** Filtered, sorted (needs-attention → recency) cross-job thread list. */
    list: (params?: InboxParams) => api.get<InboxResponse>(`${BASE}/inbox`, { params }),

    /** One thread: unified timeline + candidate/campaign context. */
    thread: (id: string) => api.get<ThreadResponse>(`${BASE}/threads/${id}`),

    /** Activity feed (sends / replies / bounces) for the context pane. */
    activity: (id: string, limit = 50) =>
        api.get<ThreadActivityResponse>(`${BASE}/threads/${id}/activity`, { params: { limit } }),

    /** Send a manual reply on the thread's account (then stop-on-reply). */
    reply: (id: string, payload: ReplyPayload) => api.post<ReplyResult>(`${BASE}/threads/${id}/reply`, payload),

    /** AI-suggested reply draft for the thread's latest inbound (never sends). */
    draftReply: (id: string, payload?: DraftReplyPayload) =>
        api.post<DraftReplyResult>(`${BASE}/threads/${id}/draft-reply`, payload || {}),

    updateStatus: (id: string, status: ConversationStatus) =>
        api.patch<{ conversation: Conversation; stopped: number }>(`${BASE}/threads/${id}/status`, { status }),

    /** Inbox-zero "Done": clear the needs-action flags without faking a reply. */
    markDone: (id: string) => api.post<{ conversation: Conversation }>(`${BASE}/threads/${id}/done`, {}),

    /** Manual interest mark (wins over the AI rollup); null clears it back to AI. */
    setInterest: (id: string, interestLevel: InterestLevel | null) =>
        api.patch<{ conversation: Conversation; stopped: number }>(`${BASE}/threads/${id}/interest`, {
            interestLevel,
        }),

    /** In-app notification counts (needs-action / escalations). */
    notifications: () => api.get<NotificationSummary>(`${BASE}/notifications`),
};

/* ─── Unmatched inbound triage (hardening) ────────────────── *
 * Inbound replies that matched no candidate (status:'unmatched' events) — the
 * hottest replies that used to vanish. `includeUnscoped` folds in null-org
 * orphans and is honored server-side only for `outreach.admin`. */

export const eventsApi = {
    /** Recent unresolved unmatched inbound replies (newest first). */
    listUnmatched: (params?: { includeUnscoped?: boolean; limit?: number }) =>
        api.get<UnmatchedEventsResponse>(`${BASE}/events/unmatched`, { params }),

    /** Mark one triaged so it drops off the list + badge. */
    resolveUnmatched: (id: string, params?: { includeUnscoped?: boolean }) =>
        api.patch<{ event: UnmatchedEvent }>(`${BASE}/events/${id}/resolve`, {}, { params }),
};

/* ─── Ops health (H11) ────────────────────────────────────── *
 * Heartbeats for the engine tick + inbound drain. The hub layout polls this and
 * shows a quiet warning banner when a loop has gone stale. */

export const opsApi = {
    health: () => api.get<OpsHealth>(`${BASE}/ops/health`),
};

/* ════════════════════════════════════════════════════════════
 * SSE helper (for later prompts — sequence generate, AI drafts).
 *
 * Mirrors the icp.api.ts pattern: raw `fetch` against an SSE endpoint with a
 * seamless one-shot JWT refresh on 401 so long-lived streams survive token
 * expiry. The axios interceptor can't help here because we stream the body.
 * ════════════════════════════════════════════════════════════ */

export interface OutreachSSEEvent {
    event: string;
    data: any;
}

const PROACTIVE_REFRESH_WINDOW_S = 60;
let inFlightRefresh: Promise<string | null> | null = null;

function decodeJwtExp(token: string): number {
    try {
        const parts = token.split('.');
        if (parts.length < 2) return 0;
        const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
        const payload = JSON.parse(atob(padded));
        return typeof payload?.exp === 'number' ? payload.exp : 0;
    } catch {
        return 0;
    }
}

async function refreshTokenOnce(): Promise<string | null> {
    try {
        const res = await api.post('/auth/refresh');
        const fresh = res?.data?.accessToken;
        if (typeof fresh === 'string' && fresh.length > 0) {
            if (typeof window !== 'undefined') localStorage.setItem(accessTokenKey, fresh);
            return fresh;
        }
        return null;
    } catch (err) {
        console.error('[outreach.api] token refresh failed:', err);
        if (typeof window !== 'undefined') localStorage.removeItem(accessTokenKey);
        return null;
    }
}

async function ensureFreshAccessToken(): Promise<string | null> {
    if (typeof window === 'undefined') return null;
    const token = localStorage.getItem(accessTokenKey);
    if (!token) return null;
    const exp = decodeJwtExp(token);
    const nowSec = Math.floor(Date.now() / 1000);
    if (exp > 0 && exp - nowSec > PROACTIVE_REFRESH_WINDOW_S) return token;
    if (!inFlightRefresh) {
        inFlightRefresh = refreshTokenOnce().finally(() => {
            inFlightRefresh = null;
        });
    }
    return inFlightRefresh;
}

async function fetchWithAuthRefresh(makeRequest: (token: string | null) => Promise<Response>): Promise<Response> {
    let token = await ensureFreshAccessToken();
    let response = await makeRequest(token);
    if (response.status === 401) {
        token = await refreshTokenOnce();
        if (token) response = await makeRequest(token);
    }
    return response;
}

/** Parse a streaming `event:`/`data:` SSE body, dispatching each event. */
async function pumpSSE(response: Response, onEvent: (event: OutreachSSEEvent) => void): Promise<void> {
    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Request failed' }));
        onEvent({ event: 'error', data: { message: err.error || 'Request failed' } });
        return;
    }
    const reader = response.body?.getReader();
    if (!reader) return;
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = '';
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
            if (line.startsWith('event: ')) {
                currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ') && currentEvent) {
                try {
                    onEvent({ event: currentEvent, data: JSON.parse(line.slice(6)) });
                } catch {
                    /* skip malformed data */
                }
                currentEvent = '';
            }
        }
    }
}

/**
 * Open an outreach SSE stream (POST). Returns an AbortController so the caller
 * can cancel. Used by later prompts for sequence generation + AI draft streams.
 *
 * @param path  path under the outreach base, e.g. `/campaigns/:id/sequence/generate`
 */
export function openOutreachStream(
    path: string,
    body: unknown,
    onEvent: (event: OutreachSSEEvent) => void
): AbortController {
    const controller = new AbortController();
    const baseUrl = getServerUrl();
    const payload = JSON.stringify(body ?? {});

    const makeRequest = (t: string | null) =>
        fetch(`${baseUrl}${BASE}${path}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                ...(t ? { Authorization: `Bearer ${t}` } : {}),
            },
            body: payload,
            signal: controller.signal,
        });

    fetchWithAuthRefresh(makeRequest)
        .then((response) => pumpSSE(response, onEvent))
        .catch((err) => {
            if (err.name !== 'AbortError') {
                onEvent({ event: 'error', data: { message: err.message } });
            }
        });

    return controller;
}

// NOTE: the live-inbox SSE feed (`openOutreachEventStream` / GET `/stream`) was
// removed — an in-memory push bus can't fan out across stateless K8s pods. The
// inbox now polls (see InboxThreePane). The POST `openOutreachStream` above stays:
// it's a request/response generation stream (sequence builder / AI draft), not a
// cross-pod pub/sub, so it's fine. Future realtime: serverless-Redis pub/sub.
